/**
 * CookieBanner — Option B (notice + opt-out) with in-place preference flip.
 *
 * Rendering rules (match /lib/consent.js):
 *   - Hidden if GPC is on (visitor already opted out at the browser level).
 *   - Shown on first visit (no stored decision).
 *   - Shown whenever the footer "Cookie preferences" button dispatches
 *     the `simonara:reopen-consent` window event.
 *
 * Interaction rules (iter-175 polish):
 *   - Both Accept and Reject are always visible.
 *   - A status chip shows the CURRENT stored preference so the visitor
 *     can see which one is active without leaving the page.
 *   - Clicking a button writes the new decision immediately and updates
 *     the chip — the banner stays open so the visitor can flip-flop as
 *     often as they want.
 *   - The X close button just dismisses the banner; it never changes
 *     consent after the initial choice is made.
 *   - If the visitor dismisses on first visit WITHOUT choosing, we leave
 *     consent null — the banner comes back on the next page load. We do
 *     not force-reject on close.
 *
 * Standing Rules: no new features, no new copy claims. The banner only
 * talks about analytics cookies and links to the existing /cookies and
 * /privacy pages.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check } from "lucide-react";
import {
    CONSENT_STATES,
    acceptConsent,
    isGPCActive,
    readConsent,
    rejectConsent,
} from "@/lib/consent";

const CookieBanner = () => {
    const [visible, setVisible] = useState(false);
    const [choice, setChoice] = useState(readConsent());

    useEffect(() => {
        if (isGPCActive()) return;
        if (readConsent() === CONSENT_STATES.UNKNOWN) setVisible(true);
        const onReopen = () => {
            if (isGPCActive()) return;
            setChoice(readConsent());
            setVisible(true);
        };
        window.addEventListener("simonara:reopen-consent", onReopen);
        return () => window.removeEventListener("simonara:reopen-consent", onReopen);
    }, []);

    if (!visible) return null;

    const applyAccept = () => {
        acceptConsent();
        setChoice(CONSENT_STATES.ACCEPTED);
    };
    const applyReject = () => {
        rejectConsent();
        setChoice(CONSENT_STATES.REJECTED);
    };
    const close = () => setVisible(false);

    const chip = (() => {
        if (choice === CONSENT_STATES.ACCEPTED) {
            return (
                <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[11px] font-medium"
                    data-testid="cookie-banner-current-state"
                >
                    <Check size={11} /> Analytics accepted
                </span>
            );
        }
        if (choice === CONSENT_STATES.REJECTED) {
            return (
                <span
                    className="inline-flex items-center gap-1 rounded-full bg-muted border border-border text-muted-foreground px-2 py-0.5 text-[11px] font-medium"
                    data-testid="cookie-banner-current-state"
                >
                    <X size={11} /> Analytics rejected
                </span>
            );
        }
        return (
            <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[11px] font-medium"
                data-testid="cookie-banner-current-state"
            >
                No preference set
            </span>
        );
    })();

    const btnClass = (active) =>
        active
            ? "btn-green text-xs px-3 py-1.5 inline-flex items-center cursor-default"
            : "btn-outline text-xs px-3 py-1.5 inline-flex items-center";

    return (
        <div
            data-testid="cookie-banner"
            role="region"
            aria-label="Cookie preferences"
            className="fixed inset-x-3 bottom-3 md:inset-x-auto md:left-6 md:bottom-6 md:max-w-xl z-[9999]
                       rounded-xl border border-border bg-card/95 backdrop-blur-sm shadow-xl p-4 sm:p-5"
        >
            <div className="flex items-start gap-3">
                <div className="shrink-0 rounded-lg bg-emerald-500/10 text-emerald-600 p-2">
                    <Cookie size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-semibold text-foreground text-sm">
                            We use cookies for analytics
                        </p>
                        {chip}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                        Simonara uses a privacy-respecting analytics tool (PostHog) to
                        understand how the product is used. No ad trackers, no
                        third-party sale of data. Change your preference any time —
                        your choice sticks. See our{" "}
                        <Link
                            to="/cookies"
                            className="underline hover:text-foreground"
                            data-testid="cookie-banner-cookies-link"
                        >
                            Cookies Policy
                        </Link>{" "}
                        and{" "}
                        <Link
                            to="/privacy"
                            className="underline hover:text-foreground"
                            data-testid="cookie-banner-privacy-link"
                        >
                            Privacy Policy
                        </Link>
                        .
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={applyAccept}
                            className={btnClass(choice === CONSENT_STATES.ACCEPTED)}
                            aria-pressed={choice === CONSENT_STATES.ACCEPTED}
                            data-testid="cookie-banner-accept"
                        >
                            {choice === CONSENT_STATES.ACCEPTED && (
                                <Check size={12} className="mr-1" />
                            )}
                            Accept
                        </button>
                        <button
                            type="button"
                            onClick={applyReject}
                            className={btnClass(choice === CONSENT_STATES.REJECTED)}
                            aria-pressed={choice === CONSENT_STATES.REJECTED}
                            data-testid="cookie-banner-reject"
                        >
                            {choice === CONSENT_STATES.REJECTED && (
                                <Check size={12} className="mr-1" />
                            )}
                            Reject
                        </button>
                        <Link
                            to="/cookies"
                            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                            data-testid="cookie-banner-settings"
                        >
                            Cookie settings
                        </Link>
                        <button
                            type="button"
                            onClick={close}
                            className="ml-auto text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                            data-testid="cookie-banner-done"
                        >
                            Done
                        </button>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close cookie preferences"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    data-testid="cookie-banner-close"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
};

export default CookieBanner;
